import { Capacitor, CapacitorHttp, registerPlugin } from "@capacitor/core";

/**
 * The Tafo app inside the app from the stores (mobile/, a Capacitor shell around these same screens).
 * In a browser none of this exists and everything below answers null: the web app works as before.
 */

/** A position as the plugin "BackgroundGeolocation" (@capacitor-community/background-geolocation) gives it. */
export interface NativePosition {
  latitude: number;
  longitude: number;
  /** Metres, 68% sure. */
  accuracy: number;
}

interface WatcherOptions {
  /** Set: the position goes on in the background, with a notification on Android that says so. */
  backgroundMessage?: string;
  backgroundTitle?: string;
  requestPermissions?: boolean;
  stale?: boolean;
  /** Metres between two positions. */
  distanceFilter?: number;
}

export interface BackgroundLocation {
  addWatcher(
    options: WatcherOptions,
    callback: (position?: NativePosition, error?: { code?: string; message?: string }) => void,
  ): Promise<string>;
  removeWatcher(options: { id: string }): Promise<void>;
}

interface LocalNotifications {
  requestPermissions(): Promise<{ display: string }>;
}

/** True only inside the app from the stores. */
export function inNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/** The phone's position that goes on with the screen off or Google Maps in front; null in a browser. */
export function backgroundLocation(): BackgroundLocation | null {
  return inNativeApp() && Capacitor.isPluginAvailable("BackgroundGeolocation")
    ? registerPlugin<BackgroundLocation>("BackgroundGeolocation")
    : null;
}

/**
 * Android 13 and later only show the notification "Tafo deelt je plaats" (without which the position
 * stops in the background) when the courier allowed notifications. Asked once, at the first ride.
 * Not on the iPhone: there the blue bar at the top shows it, and an extra question would only confuse.
 */
export async function allowNotifications(): Promise<void> {
  if (Capacitor.getPlatform() !== "android" || !Capacitor.isPluginAvailable("LocalNotifications")) return;
  try {
    await registerPlugin<LocalNotifications>("LocalNotifications").requestPermissions();
  } catch {
    // refused or not asked: the position goes on while the app is in front
  }
}

/**
 * `fetch` through the phone itself instead of the web view: Android slows down what a web view sends
 * after five minutes in the background, the phone's own connection it does not. Null in a browser.
 * Only for the requests that must go on in the background (the position); everything else stays as it is.
 */
export function nativeFetch(): typeof fetch | null {
  if (!inNativeApp()) return null;
  return async (input, init) => {
    const url = new URL(
      typeof input === "string" ? input : input instanceof URL ? input.href : input.url,
      location.href,
    );
    const headers = Object.fromEntries(new Headers(init?.headers).entries());
    const body = typeof init?.body === "string" ? (JSON.parse(init.body) as unknown) : undefined;
    const answer = await CapacitorHttp.request({
      url: url.href,
      method: init?.method ?? "GET",
      headers,
      ...(body === undefined ? {} : { data: body }),
      connectTimeout: 15_000,
      readTimeout: 15_000,
    });
    const text =
      answer.data === null || answer.data === undefined || answer.data === ""
        ? null
        : typeof answer.data === "string"
          ? answer.data
          : JSON.stringify(answer.data);
    // a response without content cannot carry a body
    const empty = answer.status === 204 || answer.status === 304;
    return new Response(empty ? null : text, { status: answer.status, headers: answer.headers });
  };
}
